import React, { useState } from 'react';
import { BRANDS, YEARS, WATCHES, MODELS_BY_BRAND, fmt, tradeValue, insuranceValue, pct } from '../data';
import ValueBadges from '../components/ValueBadges';

const CONDITIONS = ['Unworn', 'Excellent', 'Very Good', 'Good', 'Fair'];

const inp = {
  width: '100%', fontSize: 14, padding: '12px 14px', borderRadius: 10,
  border: '0.5px solid var(--border2)', background: 'var(--surface2)',
  color: 'var(--text)', outline: 'none',
  fontFamily: "'Space Grotesk', sans-serif",
  transition: 'border-color 0.15s',
};

export default function AppraisalPage({ onSelectWatch }) {
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [ref, setRef] = useState('');
  const [year, setYear] = useState('');
  const [condition, setCondition] = useState('');
  const [papers, setPapers] = useState('');
  const [service, setService] = useState('');
  const [mods, setMods] = useState('');
  const [wear, setWear] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const modelOptions = brand ? (MODELS_BY_BRAND[brand] || ['All Models']).filter(m => m !== 'All Models') : [];
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleAppraise() {
    if (!brand && !model && !ref) {
      setError('Please enter at least a brand, model, or reference number.');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);

    // Match against known watches first
    const matches = WATCHES.filter(w => {
        const brandMatch = !brand || w.brand.toLowerCase().includes(brand.toLowerCase());
        const modelMatch = !model || w.model.toLowerCase().includes(model.toLowerCase());
        const refMatch = !ref || w.ref.toLowerCase().includes(ref.toLowerCase());
        const yearMatch = !year || w.year === Number(year);
        return brandMatch && modelMatch && refMatch && yearMatch;
      });

      if (matches.length > 0) {
        const base = matches[0];

        // ── REAL APPRAISAL ENGINE ─────────────────────────────────────────
        // Each factor applies a realistic market adjustment based on
        // actual secondary market data from Chrono24, WatchCharts, and dealer experience.

        // 1. CONDITION — biggest single factor (±30%)
        const conditionAdj = {
          'Unworn':    1.08,   // sealed / never worn → premium over market
          'Excellent': 1.00,   // baseline
          'Very Good': 0.93,   // minor hairlines, light bracelet stretch
          'Good':      0.84,   // visible case wear, stretched bracelet
          'Fair':      0.71,   // significant scratches, possible service needed
        }[condition] || 1.00;

        // 2. PAPERS & BOX — major impact, especially on Rolex/Patek
        // Full set watches sell for 15-25% more on secondary market
        const papersAdj = {
          'Full set (papers + box)': 1.00,   // baseline — we price at full set value
          'Papers only':             0.93,   // -7% without box
          'Box only':                0.88,   // -12% without papers (papers matter more)
          'No papers or box':        0.82,   // -18% — significant discount especially on high-value pieces
        }[papers] || 0.88;   // default assumes no papers if not specified

        // 3. SERVICE HISTORY — affects buyer confidence
        const serviceAdj = {
          'Never serviced':       0.97,   // slightly lower — may need service soon
          'Factory serviced':     1.02,   // premium — documented factory service
          'Independent serviced': 0.98,   // minor discount vs factory
          'Unknown':              0.96,   // uncertainty discount
        }[service] || 0.97;

        // 4. MODIFICATIONS — any non-original parts kill value significantly
        const modsAdj = {
          'Unmodified':       1.00,   // full value
          'Aftermarket parts': 0.70,  // -30% — serious deduction for aftermarket
          'Custom dial':       0.60,  // -40% — destroys collector value
          'Other mods':        0.75,  // -25%
        }[mods] || 1.00;

        // 5. CASE & BRACELET WEAR — physical condition detail
        const wearAdj = {
          'None — mint':      1.03,   // sharp edges, mint bracelet → small premium
          'Light scratches':  1.00,   // baseline
          'Moderate wear':    0.95,   // noticeable but acceptable
          'Heavy wear':       0.86,   // will need polishing or bracelet links
          'Polished':         0.88,   // collectors dislike polished watches — loses sharp edges
        }[wear] || 1.00;

        // 6. AGE PREMIUM/DISCOUNT vs current year (2026)
        // Newer watches retain value better; very old watches may be collector pieces
        const watchYear = base.year || 2020;
        const age = 2026 - watchYear;
        let ageAdj = 1.00;
        if (age <= 1)       ageAdj = 1.04;  // nearly new
        else if (age <= 3)  ageAdj = 1.00;  // recent
        else if (age <= 6)  ageAdj = 0.97;  // a few years old
        else if (age <= 10) ageAdj = 0.94;  // older — minor discount
        else if (age <= 16) ageAdj = 0.90;  // 10-16 years — more meaningful discount
        else                ageAdj = 0.87;  // 17+ years — vintage territory

        // Combined market adjustment
        const totalAdj = conditionAdj * papersAdj * serviceAdj * modsAdj * wearAdj * ageAdj;

        // Build breakdown for display
        const adjustments = [];
        if (condition) adjustments.push({ label: 'Condition', factor: conditionAdj });
        if (papers)    adjustments.push({ label: 'Papers & box', factor: papersAdj });
        if (service)   adjustments.push({ label: 'Service history', factor: serviceAdj });
        if (mods)      adjustments.push({ label: 'Modifications', factor: modsAdj });
        if (wear)      adjustments.push({ label: 'Case & bracelet wear', factor: wearAdj });
        adjustments.push({ label: 'Age adjustment', factor: ageAdj });

        // ── LIVE PRICE FETCH ─────────────────────────────────────────────
        // Fire the same Chrono24 + WatchCharts + Reddit scraper used on detail pages
        let liveMarket = null;
        let liveSource = 'static';
        try {
          const params = new URLSearchParams({
            watchId: base.id,
            ref: base.ref || '',
            brand: base.brand || '',
            model: base.model || '',
          });
          const res = await fetch(`/.netlify/functions/watch-price?${params}`);
          const data = await res.json();
          if (data.market) {
            // Sanity check — must be within 40%-250% of static price
            const ratio = data.market / base.market;
            if (ratio >= 0.40 && ratio <= 2.50) {
              liveMarket = data.market;
              liveSource = data.source || 'static';
            }
          }
        } catch (e) {
          // Scrape failed silently — fall back to static
        }

        // Use live price if available, otherwise static
        const basePrice = liveMarket || base.market;
        const adjustedMarket = Math.round(basePrice * totalAdj);
        const highDemand = ['Rolex','Patek Philippe','Audemars Piguet'].includes(base.brand);
        const tradeRate = highDemand ? 0.72 : 0.62;
        const adjustedTrade = Math.round(adjustedMarket * tradeRate);
        const discontinued = base.colorway && base.colorway.toLowerCase().includes('discontinu');
        const insuranceRate = discontinued ? 1.40 : 1.28;
        const adjustedInsurance = Math.round(adjustedMarket * insuranceRate);

        // ── RANGE CALCULATIONS ───────────────────────────────────────────
        // Tight range around the adjusted midpoint — reflects real market spread
        // Better watches get tighter ranges, worse condition = wider uncertainty

        const conditionSpread = {
          'Unworn':    0.025,  // ±2.5% — very predictable, rarely listed
          'Excellent': 0.035,  // ±3.5% — small variance, clean examples trade predictably
          'Very Good': 0.050,  // ±5.0% — some variance depending on bracelet/dial
          'Good':      0.070,  // ±7.0% — buyer negotiates harder on worn pieces
          'Fair':      0.100,  // ±10% — widest range, very negotiable
        }[condition] || 0.05;

        // Market range
        const marketLow  = Math.round(adjustedMarket * (1 - conditionSpread) / 100) * 100;
        const marketHigh = Math.round(adjustedMarket * (1 + conditionSpread) / 100) * 100;

        // Trade range — dealers have a tighter band they work within
        // Low end: what a rushed sale or non-specialist dealer pays
        // High end: what a specialist dealer who wants your specific piece pays
        const highDemandTrade = ['Rolex','Patek Philippe','Audemars Piguet'].includes(base.brand);
        const tradeLow  = Math.round(adjustedMarket * (highDemandTrade ? 0.65 : 0.55) / 100) * 100;
        const tradeHigh = Math.round(adjustedMarket * (highDemandTrade ? 0.76 : 0.67) / 100) * 100;

        // Insurance range — replacement cost varies by insurer and policy type
        // Low: agreed value policy minimum
        // High: full retail replacement cost (what it costs to buy a comparable piece today)
        const insureLow  = Math.round(adjustedMarket * (discontinued ? 1.30 : 1.20) / 100) * 100;
        const insureHigh = Math.round(adjustedMarket * (discontinued ? 1.55 : 1.38) / 100) * 100;

        setResult({
          ...base,
          market: adjustedMarket,
          marketLow, marketHigh,
          tradeLow, tradeHigh,
          insureLow, insureHigh,
          trade: adjustedTrade,
          insurance: adjustedInsurance,
          baseMarket: basePrice,
          staticMarket: base.market,
          liveSource,
          liveMarket,
          conditionApplied: condition || 'Excellent',
          matchCount: matches.length,
          allMatches: matches,
          adjustments,
          totalAdj,
          conditionSpread,
        });
      } else {
        setError('No matching watch found. Try adjusting your search — we may not have this model in our database yet.');
      }
      setLoading(false);
  }

  function reset() { setBrand(''); setModel(''); setRef(''); setYear(''); setCondition(''); setPapers(''); setService(''); setMods(''); setWear(''); setPurchasePrice(''); setResult(null); setError(''); }

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: 'clamp(24px,5vw,48px) clamp(12px,4vw,20px) 60px', position: 'relative', zIndex: 1 }}>

      {/* Header */}
      <div style={{ marginBottom: 36 }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C0392B',
          background: 'rgba(192,57,43,0.08)', padding: '4px 10px', borderRadius: 99, marginBottom: 16,
          fontFamily: "'Space Mono', monospace",
        }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#C0392B', display: 'inline-block' }} />
          Instant Appraisal
        </div>
        <h1 style={{
          fontFamily: "'Space Grotesk', sans-serif", fontSize: 34, fontWeight: 700,
          letterSpacing: '-0.02em', marginBottom: 10,
        }}>What's your watch worth?</h1>
        <p style={{ fontSize: 15, color: 'var(--text2)', lineHeight: 1.7 }}>
          Enter your watch details below and get an instant valuation — market price, dealer trade value, and insurance payout.
        </p>
      </div>

      {!result ? (
        <div style={{
          background: 'var(--surface)', border: '0.5px solid var(--border2)',
          borderRadius: 'var(--radius-xl)', padding: 32,
          boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Brand</label>
              <select value={brand} onChange={e => { setBrand(e.target.value); setModel(''); }} style={inp}>
                <option value="">Select brand…</option>
                {BRANDS.filter(b => b !== 'All').map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Model</label>
              {modelOptions.length > 0 ? (
                <select value={model} onChange={e => setModel(e.target.value)} style={inp}>
                  <option value="">Select model…</option>
                  {modelOptions.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : (
                <input type="text" placeholder="e.g. Submariner, Nautilus…" value={model} onChange={e => setModel(e.target.value)} style={inp} />
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Reference Number</label>
              <input type="text" placeholder="e.g. 126610LN, 5711/1A…" value={ref} onChange={e => setRef(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Year</label>
              <select value={year} onChange={e => setYear(e.target.value)} style={inp}>
                <option value="">Select year…</option>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            {/* Condition */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Overall Condition</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CONDITIONS.map(cond => (
                  <button key={cond} onClick={() => setCondition(cond)} style={{
                    padding: '8px 16px', borderRadius: 8, border: '0.5px solid var(--border2)',
                    background: condition === cond ? 'var(--text)' : 'var(--surface2)',
                    color: condition === cond ? 'var(--surface)' : 'var(--text2)',
                    fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: condition === cond ? 600 : 400, transition: 'all 0.15s',
                  }}>{cond}</button>
                ))}
              </div>
            </div>

            {/* Papers & Box */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Papers & Documentation</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Full set (papers + box)', 'Papers only', 'Box only', 'No papers or box'].map(opt => (
                  <button key={opt} onClick={() => setPapers(opt)} style={{
                    padding: '8px 14px', borderRadius: 8, border: '0.5px solid var(--border2)',
                    background: papers === opt ? 'var(--text)' : 'var(--surface2)',
                    color: papers === opt ? 'var(--surface)' : 'var(--text2)',
                    fontSize: 12, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: papers === opt ? 600 : 400, transition: 'all 0.15s',
                  }}>{opt}</button>
                ))}
              </div>
            </div>

            {/* Service history */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Service History</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Never serviced', 'Factory serviced', 'Independent serviced', 'Unknown'].map(opt => (
                  <button key={opt} onClick={() => setService(opt)} style={{
                    padding: '7px 12px', borderRadius: 8, border: '0.5px solid var(--border2)',
                    background: service === opt ? 'var(--text)' : 'var(--surface2)',
                    color: service === opt ? 'var(--surface)' : 'var(--text2)',
                    fontSize: 12, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: service === opt ? 600 : 400, transition: 'all 0.15s',
                  }}>{opt}</button>
                ))}
              </div>
            </div>

            {/* Modifications */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Modifications</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['Unmodified', 'Aftermarket parts', 'Custom dial', 'Other mods'].map(opt => (
                  <button key={opt} onClick={() => setMods(opt)} style={{
                    padding: '7px 12px', borderRadius: 8, border: '0.5px solid var(--border2)',
                    background: mods === opt ? 'var(--text)' : 'var(--surface2)',
                    color: mods === opt ? 'var(--surface)' : 'var(--text2)',
                    fontSize: 12, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: mods === opt ? 600 : 400, transition: 'all 0.15s',
                  }}>{opt}</button>
                ))}
              </div>
            </div>

            {/* Scratches */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Case & Bracelet Wear</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {['None — mint', 'Light scratches', 'Moderate wear', 'Heavy wear', 'Polished'].map(opt => (
                  <button key={opt} onClick={() => setWear(opt)} style={{
                    padding: '7px 12px', borderRadius: 8, border: '0.5px solid var(--border2)',
                    background: wear === opt ? 'var(--text)' : 'var(--surface2)',
                    color: wear === opt ? 'var(--surface)' : 'var(--text2)',
                    fontSize: 12, cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: wear === opt ? 600 : 400, transition: 'all 0.15s',
                  }}>{opt}</button>
                ))}
              </div>
            </div>

            {/* Purchase price */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 7, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Your Purchase Price (optional)</label>
              <input type="number" placeholder="e.g. 12000" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)} style={inp} />
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(208,74,40,0.08)', border: '0.5px solid rgba(208,74,40,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: 'var(--dn)' }}>
              <i className="ti ti-alert-circle" style={{ marginRight: 6 }} />{error}
            </div>
          )}

          <button onClick={handleAppraise} disabled={loading} style={{
            width: '100%', padding: '13px', borderRadius: 10, border: 'none',
            background: loading ? 'var(--surface3)' : 'linear-gradient(135deg, #C0392B, #96281B)',
            color: loading ? 'var(--text3)' : '#fff',
            fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.03em',
            boxShadow: loading ? 'none' : '0 4px 16px rgba(192,57,43,0.3)',
            transition: 'all 0.2s',
          }}>
            {loading ? (
              <span><i className="ti ti-loader-2" style={{ marginRight: 8, display: 'inline-block', animation: 'spin 1s linear infinite' }} />Analyzing market data…</span>
            ) : (
              <span><i className="ti ti-calculator" style={{ marginRight: 8 }} />Get Instant Appraisal</span>
            )}
          </button>
        </div>
      ) : (
        <div>
          {/* Result card */}
          <div style={{
            background: 'var(--surface)', border: '0.5px solid var(--border2)',
            borderRadius: 'var(--radius-xl)', padding: 32, marginBottom: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4, fontFamily: "'Space Mono', monospace" }}>{result.brand}</div>
                <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 4 }}>{result.model}</h2>
                <div style={{ fontSize: 13, color: 'var(--text3)', fontFamily: "'Space Mono', monospace" }}>Ref. {result.ref} · {result.year} · {result.conditionApplied}</div>
                <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, fontFamily: "'Space Mono', monospace",
                  color: result.liveMarket ? '#0f9b6e' : '#9898aa' }}>
                  {result.liveMarket
                    ? `● Sold listings · ${result.liveSource
                        .replace('ebay-sold','eBay Sold')
                        .replace('watchcharts','WatchCharts')
                        .replace('reddit-sold','Reddit WatchExchange')
                        .replace('chrono24','Chrono24')
                        .replace(/\+/g,' + ')}`
                    : '● Curated market estimate · May 2026'}
                </div>
              </div>
              <div style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 99,
                background: result.market > result.msrp ? 'rgba(15,155,110,0.1)' : 'rgba(208,74,40,0.1)',
                color: result.market > result.msrp ? 'var(--up)' : 'var(--dn)',
                fontWeight: 600, fontFamily: "'Space Mono', monospace",
              }}>{pct(result.market, result.msrp)} vs retail MSRP</div>
            </div>

            {/* Base vs adjusted */}
            {(result.baseMarket !== result.market || result.liveMarket) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: "'Space Mono', monospace" }}>
                  {result.liveMarket ? 'Live scraped price:' : 'Base market price:'}
                </span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.baseMarket)}</span>
                <span style={{ fontSize: 11, color: result.market > result.baseMarket ? 'var(--up)' : 'var(--dn)', fontFamily: "'Space Mono', monospace", fontWeight: 600 }}>
                  {result.market > result.baseMarket ? '▲' : '▼'} {Math.abs(Math.round((result.totalAdj - 1) * 100))}% after adjustments
                </span>
              </div>
            )}

            {/* Three value range boxes */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>

              {/* Market Value Range */}
              <div style={{ background: 'rgba(24,95,165,0.07)', borderRadius: 10, padding: '16px 18px', border: '0.5px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--market)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: "'Space Mono', monospace" }}>
                      Market Value
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--market)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.marketLow || result.market)}</span>
                      <span style={{ fontSize: 16, color: 'var(--text3)', fontWeight: 400 }}>—</span>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--market)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.marketHigh || result.market)}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>What this watch sells for on the secondary market today</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>MID POINT</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--market)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.market)}</div>
                  </div>
                </div>
              </div>

              {/* Trade Value Range */}
              <div style={{ background: 'rgba(60,52,137,0.07)', borderRadius: 10, padding: '16px 18px', border: '0.5px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--trade)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: "'Space Mono', monospace" }}>
                      Dealer Trade-In Offer
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--trade)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.tradeLow || Math.round(result.market * 0.65))}</span>
                      <span style={{ fontSize: 16, color: 'var(--text3)', fontWeight: 400 }}>—</span>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--trade)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.tradeHigh || Math.round(result.market * 0.75))}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>What a dealer will offer — low end is a quick sale, high end is a specialist who wants it</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>VS MARKET</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--trade)', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {Math.round(((result.tradeLow || result.market * 0.65) / result.market) * 100)}–{Math.round(((result.tradeHigh || result.market * 0.75) / result.market) * 100)}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance Range */}
              <div style={{ background: 'rgba(15,110,86,0.07)', borderRadius: 10, padding: '16px 18px', border: '0.5px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--insure)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: "'Space Mono', monospace" }}>
                      Insurance Coverage
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--insure)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.insureLow || Math.round(result.market * 1.20))}</span>
                      <span style={{ fontSize: 16, color: 'var(--text3)', fontWeight: 400 }}>—</span>
                      <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--insure)', fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.insureHigh || Math.round(result.market * 1.38))}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Recommended policy coverage — ensure you are not underinsured</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'Space Mono', monospace", marginBottom: 2 }}>OF MARKET</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--insure)', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {Math.round(((result.insureLow || result.market * 1.20) / result.market) * 100)}–{Math.round(((result.insureHigh || result.market * 1.38) / result.market) * 100)}%
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Adjustment breakdown */}
            {result.adjustments && result.adjustments.length > 0 && (
              <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 12, fontFamily: "'Space Mono', monospace" }}>
                  Valuation adjustments applied
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {result.adjustments.map((adj, i) => {
                    const impact = Math.round((adj.factor - 1) * 100);
                    const positive = adj.factor >= 1;
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text2)', fontFamily: "'Space Grotesk', sans-serif" }}>{adj.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: positive ? 'var(--up)' : 'var(--dn)', fontFamily: "'Space Mono', monospace" }}>
                          {positive ? '+' : ''}{impact}%
                        </span>
                      </div>
                    );
                  })}
                  <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', fontFamily: "'Space Grotesk', sans-serif" }}>Net adjustment</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: result.totalAdj >= 1 ? 'var(--up)' : 'var(--dn)', fontFamily: "'Space Mono', monospace" }}>
                      {result.totalAdj >= 1 ? '+' : ''}{Math.round((result.totalAdj - 1) * 100)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Purchase price gain/loss */}
            {purchasePrice && Number(purchasePrice) > 0 && (
              <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10, fontFamily: "'Space Mono', monospace" }}>
                  Your return
                </div>
                {(() => {
                  const paid = Number(purchasePrice);
                  const gain = result.market - paid;
                  const gainPct = Math.round((gain / paid) * 100);
                  return (
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>You paid</div>
                        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(paid)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Current value</div>
                        <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif" }}>{fmt(result.market)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 2 }}>Gain / Loss</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: gain >= 0 ? 'var(--up)' : 'var(--dn)', fontFamily: "'Space Grotesk', sans-serif" }}>
                          {gain >= 0 ? '+' : ''}{fmt(gain)} ({gainPct >= 0 ? '+' : ''}{gainPct}%)
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
              <i className="ti ti-info-circle" style={{ marginRight: 6 }} />
              Valuations reflect current secondary market data adjusted for your watch's specific condition. For certified insurance or resale documentation, consult a licensed appraiser.
            </div>
          </div>

          {result.matchCount > 1 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 10 }}>
                <i className="ti ti-list" style={{ marginRight: 6 }} />
                {result.matchCount} matching watches found — showing closest match
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={reset} style={{
              flex: 1, padding: '11px', borderRadius: 10, border: '0.5px solid var(--border2)',
              background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 500,
            }}>
              <i className="ti ti-refresh" style={{ marginRight: 6 }} />New appraisal
            </button>
            <button onClick={() => onSelectWatch(result)} style={{
              flex: 1, padding: '11px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #C0392B, #96281B)',
              color: '#fff', fontSize: 13, cursor: 'pointer',
              fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600,
              boxShadow: '0 4px 16px rgba(192,57,43,0.25)',
            }}>
              <i className="ti ti-chart-line" style={{ marginRight: 6 }} />View full price history
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
